import React, { useEffect, useState } from 'react';
import api from '../api/client';
import { Link } from 'react-router-dom';
import {
  DoorOpen, UserPlus, Package, ClipboardList, IdCard, TrendingUp,
  AlertTriangle, ArrowRight, ShieldCheck, ScanLine, LogIn, LogOut,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';

function Stat({ label, value, hint, icon: Icon, accent }) {
  return (
    <div className="card p-5 card-hover fade-up" data-testid={`stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-start justify-between">
        <div className="overline">{label}</div>
        <div className={`w-8 h-8 flex items-center justify-center rounded-sm ${accent || 'bg-zinc-100 text-zinc-700'}`}>
          <Icon size={16} />
        </div>
      </div>
      <div className="heading text-4xl font-black tracking-tight text-ink mt-4">{value}</div>
      {hint && <div className="text-xs text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

function QuickAction({ to, label, icon: Icon, desc, state }) {
  return (
    <Link to={to} state={state} className="card p-4 card-hover flex items-center gap-3 group" data-testid={`quick-${to.replace('/','')}`}>
      <div className="w-10 h-10 bg-zinc-900 text-white flex items-center justify-center rounded-sm group-hover:bg-primary transition-colors">
        <Icon size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-ink">{label}</div>
        {desc && <div className="text-xs text-zinc-500 truncate">{desc}</div>}
      </div>
      <ArrowRight size={14} className="text-zinc-400 group-hover:text-primary transition-colors" />
    </Link>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [recentPasses, setRecentPasses] = useState([]);
  const [recentVisitors, setRecentVisitors] = useState([]);
  const [pending, setPending] = useState([]);

  const load = async () => {
    try {
      if (['admin', 'hr', 'supervisor', 'guard'].includes(user.role)) {
        const { data } = await api.get('/reports/summary');
        setSummary(data);
      }
      const { data: gps } = await api.get('/gate-passes');
      setRecentPasses(gps.slice(0, 6));
      // Recent visitors panel is only shown to hr/guard (supervisor/admin see pending approvals instead;
      // supervisors no longer have visitor-list access).
      if (!['employee', 'supervisor'].includes(user.role)) {
        const { data: vis } = await api.get('/visitors');
        setRecentVisitors(vis.slice(0, 5));
      }
      if (['admin', 'supervisor'].includes(user.role)) {
        const { data: pend } = await api.get('/gate-passes?status=pending');
        setPending(pend.slice(0, 5));
      }
    } catch {}
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [user.role]);

  const quickActions = (() => {
    switch (user.role) {
      case 'guard':
        return [
          { to: '/scanner', label: 'Scan QR / Passcode', icon: ScanLine, desc: 'Verify a gate pass' },
          { to: '/visitors', label: 'New Visitor Entry', icon: UserPlus, desc: 'Manual visitor form' },
          { to: '/gate-passes', label: 'Gate Pass Log', icon: DoorOpen, desc: 'Track all passes' },
          { to: '/item-passes', label: 'Item Passes', icon: Package, desc: 'Return tracking' },
        ];
      case 'hr':
        return [
          { to: '/candidates', label: 'Candidates', icon: ClipboardList, desc: 'Review applications' },
          { to: '/monthly-passes', label: 'Monthly Passes', icon: IdCard, desc: 'Issue & renew' },
        ];
      case 'supervisor':
        return [
          { to: '/gate-passes', label: 'Create My Gate Pass', icon: DoorOpen, state: { openCreate: true } },
          { to: '/approvals', label: 'Pending Approvals', icon: ShieldCheck, desc: 'Review requests' },
          { to: '/gate-passes', label: 'My Passes', icon: DoorOpen },
        ];
      case 'employee':
        return [
          { to: '/gate-passes', label: 'Request Gate Pass', icon: DoorOpen, desc: 'Exit the factory' },
          { to: '/monthly-passes', label: 'My Monthly Pass', icon: IdCard, desc: 'View your pass' },
          { to: '/item-passes', label: 'Item Passes', icon: Package, desc: 'Material movements' },
        ];
      default:
        return [
          { to: '/gate-passes', label: 'Create My Gate Pass', icon: DoorOpen, desc: 'Instant approval — no waiting', state: { openCreate: true } },
          { to: '/admin', label: 'Admin Panel', icon: ShieldCheck, desc: 'Users, departments, titles' },
          { to: '/approvals', label: 'Pending Approvals', icon: ShieldCheck, desc: 'Supervisor queue' },
          { to: '/reports', label: 'Reports', icon: TrendingUp, desc: '5 operational reports with export' },
        ];
    }
  })();

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Stats */}
      {summary && (
        // Same four cards, labels and colour accents as the mobile home screen
        // (mobile/app/(tabs)/index.js), for the same roles: admin, hr, supervisor, guard.
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-grid">
          <Stat label="Today's Entries" value={summary.today?.entries ?? 0} icon={LogIn} accent="bg-green-100 text-green-700" />
          <Stat label="Today's Exit" value={summary.today?.exits ?? 0} icon={LogOut} accent="bg-orange-100 text-primary" />
          <Stat label="Visitors In" value={summary.visitors.inside} icon={UserPlus} accent="bg-blue-100 text-blue-700" />
          <Stat label="Item Due" value={summary.items.pending_return} icon={Package} accent="bg-red-100 text-red-700" />
        </div>
      )}

      {/* Role Banner */}
      <div className="card p-5 bg-gradient-to-br from-zinc-900 to-ink text-white relative overflow-hidden" data-testid="role-banner">
        <div className="absolute inset-0 bg-grid opacity-10" />
        <div className="relative flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="overline text-white/60">{user.role.toUpperCase()} Console</div>
            <div className="heading text-2xl md:text-3xl font-black mt-1">
              {user.role === 'guard' && 'Gate tower is live. Verify every exit.'}
              {user.role === 'hr' && 'Hiring pipeline & employee identity.'}
              {user.role === 'supervisor' && 'Your desk, your signature'}
              {user.role === 'employee' && 'Request passes, track your movements.'}
              {user.role === 'admin' && 'Full-system oversight of operations.'}
            </div>
          </div>
          <div className="flex items-center gap-2 text-white/80 text-xs">
            <span className="w-2 h-2 bg-green-400 rounded-full pulse-dot" />
            <span className="overline text-white/80">LIVE</span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="overline">Quick Actions</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {quickActions.map((a) => <QuickAction key={a.label} {...a} />)}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Gate Passes */}
        <section className="card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
            <h3 className="overline">Recent Gate Passes</h3>
            {/* For supervisors this box shows their team's passes, so "View all" opens the team history. */}
            <Link to={user.role === 'supervisor' ? '/approvals?status=all' : '/gate-passes'} className="text-xs text-primary font-bold hover:underline" data-testid="link-all-passes">View all →</Link>
          </div>
          <div className="divide-y divide-zinc-100">
            {recentPasses.length === 0 && (
              <div className="p-6 text-center text-sm text-zinc-400">No gate passes yet</div>
            )}
            {recentPasses.map((p) => (
              <div key={p.id} className="p-4 flex items-center gap-3" data-testid={`recent-pass-${p.pass_id}`}>
                <div className={`w-1 h-10 ${
                  p.status === 'pending' ? 'bg-yellow-400' :
                  p.status === 'approved' ? 'bg-blue-400' :
                  p.status === 'out' ? 'bg-orange-500' :
                  p.status === 'closed' ? 'bg-green-500' :
                  p.status === 'rejected' ? 'bg-red-500' : 'bg-zinc-300'
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-bold text-sm text-ink">{p.employee_name}</div>
                    <span className="kbd">{p.pass_id}</span>
                  </div>
                  <div className="text-xs text-zinc-500 truncate">{p.reason} · {p.department}</div>
                </div>
                <StatusBadge status={p.status} />
              </div>
            ))}
          </div>
        </section>

        {/* Pending approvals OR Recent visitors */}
        {['admin','supervisor'].includes(user.role) ? (
          <section className="card">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
              <h3 className="overline flex items-center gap-2">
                <AlertTriangle size={14} className="text-yellow-600" />
                Pending Approvals
              </h3>
              <Link to="/approvals" className="text-xs text-primary font-bold hover:underline">Review →</Link>
            </div>
            <div className="divide-y divide-zinc-100">
              {pending.length === 0 && (
                <div className="p-6 text-center text-sm text-zinc-400">Queue clear — nothing pending</div>
              )}
              {pending.map((p) => (
                <div key={p.id} className="p-4 flex items-center gap-3">
                  <div className="w-8 h-8 bg-yellow-100 text-yellow-800 rounded-sm flex items-center justify-center text-xs font-bold">
                    {p.pass_type === 'official' ? 'OF' : 'PR'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-ink">{p.employee_name}</div>
                    <div className="text-xs text-zinc-500 truncate">{p.reason}</div>
                  </div>
                  <div className="text-[10px] text-zinc-400">
                    {p.created_at && formatDistanceToNow(new Date(p.created_at), { addSuffix: true })}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : user.role !== 'employee' ? (
          <section className="card">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
              <h3 className="overline">Recent Visitors</h3>
              <Link to="/visitors" className="text-xs text-primary font-bold hover:underline">View all →</Link>
            </div>
            <div className="divide-y divide-zinc-100">
              {recentVisitors.length === 0 && (
                <div className="p-6 text-center text-sm text-zinc-400">No visitors yet</div>
              )}
              {recentVisitors.map((v) => (
                <div key={v.id} className="p-4 flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 text-blue-700 rounded-sm flex items-center justify-center text-xs font-bold">
                    {(v.name || '?').slice(0,1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-ink truncate">{v.name}</div>
                    <div className="text-xs text-zinc-500 truncate">{v.visitor_type} · {v.purpose || '—'}</div>
                  </div>
                  <StatusBadge status={v.status} />
                </div>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: 'badge-warn',
    approved: 'badge-info',
    out: 'badge-primary',
    closed: 'badge-success',
    rejected: 'badge-danger',
    checked_in: 'badge-info',
    checked_out: 'badge-success',
    returned: 'badge-success',
    completed: 'badge-success',
  };
  return <span className={map[status] || 'badge-muted'}>{(status || '').replace('_', ' ')}</span>;
}

export { StatusBadge };
